/**
 * Branje Icecastovega `status-json.xsl` (T3 §3).
 *
 * Čista funkcija: odgovor pride iz omrežja, izbira pravega vira in izpeljava
 * skladbe pa sta odločitvi, ki morata biti testabilni brez njega.
 *
 * ⚠️ Oblika ni enotna. `icestats.source` je **objekt**, kadar postaja oddaja
 * en sam tok, in **polje**, kadar jih oddaja več. Kdor bere le polje, pri
 * polovici postaj dobi nič.
 */

import { parseStreamTitle, type Track } from './icy-parse';

export interface IcecastSource {
  listenurl?: string;
  /** Trenutna skladba; pri nekaterih postavitvah že razdeljena v `artist`. */
  title?: string;
  artist?: string;
  server_name?: string;
  server_description?: string;
  genre?: string;
  bitrate?: number;
  audio_bitrate?: number;
}

interface IcecastStatus {
  icestats?: {
    source?: IcecastSource | IcecastSource[];
  };
}

function sources(json: unknown): IcecastSource[] {
  const source = (json as IcecastStatus | null)?.icestats?.source;
  if (source === undefined || source === null) return [];
  return Array.isArray(source) ? source : [source];
}

function pathOf(url: string | undefined): string | null {
  if (url === undefined || url === '') return null;
  try {
    return new URL(url).pathname.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/**
 * Izbere vir, ki pripada našemu toku.
 *
 * Primerja se **pot**, ne cel naslov: `listenurl` postaje pogosto navaja
 * notranje ime gostitelja ali `http:`, medtem ko mi poslušamo `https:` prek
 * druge domene ali prek preusmeritve.
 *
 * Kadar postaja oddaja en sam tok, se vzame ta — takrat ni česa zamešati.
 */
export function pickIcecastSource(json: unknown, streamUrl: string): IcecastSource | null {
  const all = sources(json);
  if (all.length === 0) return null;
  if (all.length === 1) return all[0] ?? null;

  const wanted = pathOf(streamUrl);
  if (wanted === null) return null;

  return all.find((source) => pathOf(source.listenurl) === wanted) ?? null;
}

/**
 * Skladba iz Icecastovega odgovora.
 *
 * Kadar postaja pošlje ločen `artist`, mu verjamemo — razčlenjevanje bi
 * podatek, ki je že razdeljen, lahko le pokvarilo (naslov z vezajem).
 */
export function trackFromIcecast(json: unknown, streamUrl: string): Track | null {
  const source = pickIcecastSource(json, streamUrl);
  if (source === null) return null;

  const title = source.title ?? '';
  const artist = source.artist ?? '';

  if (artist.trim() !== '' && title.trim() !== '') {
    const parsed = parseStreamTitle(`${artist} - ${title}`);
    return parsed === null ? null : { ...parsed, raw: `${artist} - ${title}` };
  }

  return parseStreamTitle(title);
}
